// Presentational view of a Drive detail (kind: "drive").
//
// Reading order down the page:
//   1. Header    — ID, title, address / date / donor, cause chips
//   2. Carousel  — the drive's photos
//   3. Figures   — what it delivered, beside the location minimap
//   4. Trees     — a table of every tree, each ID linking to its own page
// Every control is a real, always-visible tap target (no hover-only actions).

import { Sprout, Utensils, HeartHandshake, MapPin, Calendar, User } from "lucide-react";
import { formatDate } from "@/lib/date";
import LocationMap from "@/components/LocationMap";
import DrivePhotoCarousel from "@/components/tracker/DrivePhotoCarousel";
import DriveTreesTable from "@/components/tracker/DriveTreesTable";
import CertificateButton from "@/components/tracker/CertificateButton";

function CauseChip({ icon: Icon, label }) {
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-seedh-surface-green/60 text-seedh-text px-3 py-1.5 text-xs font-medium whitespace-nowrap">
            <Icon className="w-3.5 h-3.5 text-seedh-green shrink-0" /> {label}
        </span>
    );
}

function Figure({ icon: Icon, value, label }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl border border-seedh-border bg-white px-5 py-4">
            <span className="w-11 h-11 rounded-xl bg-seedh-surface-green/60 flex items-center justify-center shrink-0">
                <Icon className="w-5 h-5 text-seedh-green" />
            </span>
            <span className="min-w-0">
                <span className="block metric-figure text-3xl leading-none text-seedh-blue tabular">{value}</span>
                <span className="block text-xs text-seedh-muted mt-1.5">{label}</span>
            </span>
        </div>
    );
}

function MetaItem({ icon: Icon, children }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <Icon className="w-4 h-4 text-seedh-green shrink-0" />
            {children}
        </span>
    );
}

export default function DriveDetailView({ data }) {
    const causes = data.causes ?? {};
    const images = data.images ?? [];
    const trees = data.trees?.items ?? [];
    const treeCount = data.trees?.count ?? 0;

    const figures = [];
    if (treeCount > 0) figures.push({ icon: Sprout, value: treeCount, label: treeCount === 1 ? "tree planted" : "trees planted" });
    if (data.food) figures.push({ icon: Utensils, value: data.food.packets, label: "food packets distributed" });
    if (data.children) figures.push({ icon: HeartHandshake, value: data.children.count, label: "children supported" });

    return (
        <div className="space-y-10 md:space-y-12">
            {/* ---------- Header ---------- */}
            <header>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-3 justify-between">
                    <span className="inline-block font-mono text-xs tracking-wide bg-seedh-surface-blue text-seedh-blue rounded-full px-3 py-1.5">
                        {data.id}
                    </span>
                    {(causes.trees > 0 || causes.foodPackets > 0 || causes.children > 0) && (
                        <div className="flex flex-wrap items-center gap-2">
                            {causes.trees > 0 && (
                                <CauseChip icon={Sprout} label={`${causes.trees} tree${causes.trees === 1 ? "" : "s"}`} />
                            )}
                            {causes.foodPackets > 0 && (
                                <CauseChip icon={Utensils} label={`${causes.foodPackets} food packets`} />
                            )}
                            {causes.children > 0 && (
                                <CauseChip icon={HeartHandshake} label={`${causes.children} children`} />
                            )}
                        </div>
                    )}
                </div>

                <h1 className="font-display text-3xl md:text-4xl lg:text-[2.75rem] leading-tight text-seedh-text mt-5">
                    {data.title}
                </h1>

                {(data.location || data.date || data.donor) && (
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-sm text-seedh-muted">
                        {data.location && <MetaItem icon={MapPin}>{data.location}</MetaItem>}
                        {data.date && <MetaItem icon={Calendar}>{formatDate(data.date)}</MetaItem>}
                        {data.donor && <MetaItem icon={User}>{data.donor}</MetaItem>}
                    </div>
                )}

                {data.description && (
                    <p className="mt-6 text-base text-seedh-text/80 max-w-2xl leading-relaxed">
                        {data.description}
                    </p>
                )}

                <div className="mt-6">
                    <CertificateButton kind="drive" data={data} />
                </div>
            </header>

            {/* ---------- Photos ---------- */}
            {images.length > 0 && <DrivePhotoCarousel images={images} alt={data.title} />}

            {/* ---------- What it delivered + where ---------- */}
            {(figures.length > 0 || data.location) && (
                <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                    {figures.length > 0 && (
                        <div className="lg:col-span-2">
                            <h2 className="text-[0.7rem] tracking-[0.22em] uppercase text-seedh-brown font-semibold mb-3">
                                What this drive delivered
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {figures.map((f) => (
                                    <Figure key={f.label} icon={f.icon} value={f.value} label={f.label} />
                                ))}
                            </div>
                        </div>
                    )}

                    {(data.mapsQuery || data.location) && (
                        <div className={figures.length > 0 ? "lg:col-span-1" : "lg:col-span-2 max-w-md"}>
                            <h2 className="text-[0.7rem] tracking-[0.22em] uppercase text-seedh-brown font-semibold mb-3">
                                Where it happened
                            </h2>
                            <LocationMap location={data.location} mapsUrl={data.mapsUrl} mapsQuery={data.mapsQuery} />
                        </div>
                    )}
                </section>
            )}

            {/* ---------- Trees ---------- */}
            {treeCount > 0 && (
                <section>
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                        <h2 className="font-display text-2xl text-seedh-text">
                            Trees in this drive
                        </h2>
                        <span className="text-sm text-seedh-muted">
                            {treeCount} tracked — tap an ID to follow that tree
                        </span>
                    </div>
                    <DriveTreesTable trees={trees} />
                </section>
            )}
        </div>
    );
}
